/**
 * PlanIncludesTable (iter1078) — „Какво точно включва всеки план".
 * Данните идват от единствения източник на истина в бекенда
 * (`/api/pricing/catalog` + `/api/usage/trial/limits`), за да не се разминават
 * страницата с цените и реалните ограничения.
 *
 * Телефон: избирате план и виждате списък. Едър екран: пълна таблица.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, Minus, Coins, Clock, Loader2 } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_IDS = ["free", "pro", "clinic_lite", "clinic"];

const T = {
  bg: {
    title: "Какво точно включва всеки план",
    sub: "Числата идват направо от системата — същите, които важат в приложението.",
    included: "Включено",
    credits: (n) => `С кредити (${n})`,
    no: "Не е налично",
    plan: "Възможност",
    perMonth: "AI анализа на месец",
    trialTitle: "Какво включва пробният период",
    trialSub: (d) => `${d} дни пълен достъп, без карта. Всяка възможност има свой лимит:`,
    analyses: "анализа",
    legend: "„С кредити“ значи, че възможността не е в плана, но може да я ползвате с кредитите си.",
  },
  en: {
    title: "What each plan includes",
    sub: "Numbers come straight from the system — the same ones enforced in the app.",
    included: "Included",
    credits: (n) => `With credits (${n})`,
    no: "Not available",
    plan: "Capability",
    perMonth: "AI analyses per month",
    trialTitle: "What the trial includes",
    trialSub: (d) => `${d} days of full access, no card. Each capability has its own cap:`,
    analyses: "analyses",
    legend: "“With credits” means the capability isn’t in the plan but can be used with your credits.",
  },
};

export default function PlanIncludesTable() {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language !== "en" && language !== "es";
  const t = bg ? T.bg : T.en;

  const [catalog, setCatalog] = useState(null);
  const [trial, setTrial] = useState(null);
  const [active, setActive] = useState("pro");

  useEffect(() => {
    axios.get(`${API}/pricing/catalog`).then((r) => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/usage/trial/limits`).then((r) => setTrial(r.data)).catch(() => {});
  }, []);

  const plans = useMemo(
    () => (catalog?.plans || []).filter((p) => PLAN_IDS.includes(p.id)),
    [catalog]
  );
  const features = useMemo(() => Object.entries(catalog?.features || {}), [catalog]);

  const cellFor = (f, planId) => {
    if ((f.available_in || []).includes(planId)) return { kind: "yes", text: t.included };
    if (f.allow_via_credits && planId !== "free") return { kind: "credits", text: t.credits(f.credit_cost) };
    return { kind: "no", text: t.no };
  };

  const card = isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)]" : "bg-white border-slate-200";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  if (!catalog) {
    return (
      <div className="py-10 flex justify-center" data-testid="plan-includes-loading">
        <Loader2 className={`w-5 h-5 animate-spin ${muted}`} />
      </div>
    );
  }

  const Mark = ({ cell }) =>
    cell.kind === "yes" ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-500">
        <Check className="w-3.5 h-3.5" /> {cell.text}
      </span>
    ) : cell.kind === "credits" ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-500">
        <Coins className="w-3.5 h-3.5" /> {cell.text}
      </span>
    ) : (
      <span className={`inline-flex items-center gap-1 text-[11px] ${muted}`}>
        <Minus className="w-3.5 h-3.5" /> {cell.text}
      </span>
    );

  return (
    <div className="py-12" data-testid="plan-includes-table">
      <h2 className={`text-2xl sm:text-3xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
        {t.title}
      </h2>
      <p className={`mt-2 text-sm ${muted}`}>{t.sub}</p>

      {/* Телефон — избор на план + списък */}
      <div className="md:hidden mt-5">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              data-testid={`plan-includes-chip-${p.id}`}
              className={`shrink-0 rounded-full px-4 min-h-[40px] text-[11px] font-bold border transition-all ${
                active === p.id
                  ? "bg-gradient-to-r from-[#7B1FA2] to-[#C2185B] text-white border-transparent"
                  : isDark
                  ? "border-slate-700 text-slate-300"
                  : "border-slate-200 text-slate-600"
              }`}
            >
              {bg ? p.name_bg : p.name_en}
            </button>
          ))}
        </div>

        {plans
          .filter((p) => p.id === active)
          .map((p) => (
            <div key={p.id} className={`mt-3 rounded-2xl border p-4 ${card}`}>
              <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? p.name_bg : p.name_en}
              </p>
              <p className={`text-[11px] ${muted}`}>
                {p.credits_included || 0} {t.perMonth}
              </p>
              <ul className="mt-3 space-y-2.5">
                {features.map(([fid, f]) => (
                  <li key={fid} className="flex flex-col gap-0.5">
                    <span className={`text-[13px] font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                      {bg ? f.label_bg : f.label_en}
                    </span>
                    <Mark cell={cellFor(f, p.id)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>

      {/* Едър екран — пълна таблица */}
      <div className={`hidden md:block mt-6 rounded-2xl border overflow-hidden ${card}`}>
        <table className="w-full text-left">
          <thead className={isDark ? "bg-slate-900/60" : "bg-slate-50"}>
            <tr>
              <th className={`px-4 py-3 text-xs font-bold uppercase tracking-wide ${muted}`}>{t.plan}</th>
              {plans.map((p) => (
                <th key={p.id} className={`px-4 py-3 text-xs font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? p.name_bg : p.name_en}
                  <span className={`block text-[10px] font-medium ${muted}`}>
                    {p.credits_included || 0} {t.perMonth}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map(([fid, f], i) => (
              <tr key={fid} className={i % 2 ? (isDark ? "bg-slate-900/30" : "bg-slate-50/60") : ""}>
                <td className={`px-4 py-3 text-[13px] font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                  {bg ? f.label_bg : f.label_en}
                  <span className={`block text-[11px] font-normal ${muted}`}>
                    {bg ? f.value_bg : f.value_en}
                  </span>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className="px-4 py-3">
                    <Mark cell={cellFor(f, p.id)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`mt-3 text-[11px] ${muted}`}>{t.legend}</p>

      {/* Пробен период */}
      {trial && (
        <div className={`mt-8 rounded-2xl border p-4 sm:p-6 ${card}`} data-testid="trial-limits-card">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-500" />
            <h3 className={`text-base md:text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {t.trialTitle}
            </h3>
          </div>
          <p className={`mt-1 text-[12px] ${muted}`}>{t.trialSub(trial.duration_days)}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {trial.modules.map((m) => (
              <div
                key={m.id}
                className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ${
                  isDark ? "bg-slate-900/50" : "bg-slate-50"
                }`}
                data-testid={`trial-limit-${m.id}`}
              >
                <span className={`text-[12px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {bg ? m.label_bg : m.label_en}
                </span>
                <span className="text-[11px] font-bold text-sky-500 whitespace-nowrap">
                  {m.limit} {t.analyses}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
